import type { ReactNode } from 'react';
import type { FieldValues, Path, UseFormReturn } from 'react-hook-form';
import { Box } from '@mui/material';
import { RhfSelect, RhfTextField } from '@core/components';
import { useLastShown } from '@core/hooks/useLastShown';
import type { Vestment } from '../../api/vestment';
import { MaterialKind } from '../enum/materialKind';
import { CollapsibleField } from '../pages/OrderForm/CollapsibleField';
import { RhfCollarSelect } from '../pages/OrderForm/RhfCollarSelect';
import { RhfColorSelect } from '../pages/OrderForm/RhfColorSelect';
import { RhfFabricSelect } from '../pages/OrderForm/RhfFabricSelect';
import { RhfMaterialSelect } from '../pages/OrderForm/RhfMaterialSelect';
import {
  type ChasubleFeaturesSource,
  type ChasubleMaterialField,
  type ChasublePanelField,
  GOTHIC_TYPE_OPTIONS,
  PATCHING_TYPE_OPTIONS,
  getChasubleMaterialField,
  getChasublePanelFields,
  hasChasublePanelFabric,
} from '../utils/chasuble';

const rowSx = {
  display: 'flex',
  flexDirection: { xs: 'column', md: 'row' },
  gap: 2,
} as const;

const MATERIAL_SELECTS: Record<
  ChasubleMaterialField,
  { label: string; kind: MaterialKind }
> = {
  thread: { label: 'Thread', kind: MaterialKind.Thread },
  trimming: { label: 'Trimming', kind: MaterialKind.Trimming },
  galoon: { label: 'Galoon', kind: MaterialKind.Galoon },
};

type BodyFabricValues = FieldValues & {
  bodyFabric: string;
  bodyColor: string;
};

type PanelValues = FieldValues & {
  gothicType: string;
  patchingType: string;
  panelFabric: string;
  panelColor: string;
  liningFabric: string;
  liningColor: string;
  galoon: string;
  trimming: string;
  thread: string;
  panelDimension: string;
  stoleSize: string;
};

type CollarValues = FieldValues & {
  collar: string;
  collarFabric: string;
  collarColor: string;
};

export function ChasubleBodyFabricFields<T extends BodyFabricValues>() {
  return (
    <Box sx={rowSx}>
      <RhfFabricSelect<T>
        name={'bodyFabric' as Path<T>}
        label="Fabric"
        flag="isChasubleBody"
      />
      <RhfColorSelect<T>
        name={'bodyColor' as Path<T>}
        label="Color"
        fabricFieldName={'bodyFabric' as Path<T>}
      />
    </Box>
  );
}

export function ChasublePanelMaterialFields<T extends PanelValues>({
  methods,
  source,
  children,
}: {
  methods: UseFormReturn<T>;
  source: ChasubleFeaturesSource;
  children?: (fields: Set<ChasublePanelField>) => ReactNode;
}) {
  const gothicType = methods.watch('gothicType' as Path<T>);
  const patchingType = methods.watch('patchingType' as Path<T>);
  const optionValues = {
    gothicType: String(gothicType ?? ''),
    patchingType: String(patchingType ?? ''),
  };
  const fields = new Set(getChasublePanelFields(source, optionValues));
  const materialField = getChasubleMaterialField(source, optionValues);
  const shownMaterialField = useLastShown(materialField);
  const materialSelect = shownMaterialField ? (
    <RhfMaterialSelect<T>
      key={shownMaterialField}
      name={shownMaterialField as Path<T>}
      {...MATERIAL_SELECTS[shownMaterialField]}
    />
  ) : null;
  const fabricRow = (
    <Box sx={rowSx}>
      <RhfFabricSelect<T>
        name={'panelFabric' as Path<T>}
        label="Fabric"
        flag="isChasublePanel"
      />
      <RhfColorSelect<T>
        name={'panelColor' as Path<T>}
        label="Color"
        fabricFieldName={'panelFabric' as Path<T>}
      />
    </Box>
  );

  return (
    <>
      {fields.has('gothicType') ? (
        <Box sx={rowSx}>
          <RhfSelect<T>
            name={'gothicType' as Path<T>}
            label="Type"
            options={GOTHIC_TYPE_OPTIONS}
            onValueChange={(value) => {
              if (!hasChasublePanelFabric(value)) {
                methods.setValue('panelFabric' as Path<T>, '' as never);
                methods.setValue('panelColor' as Path<T>, '' as never);
              }
            }}
          />
          <CollapsibleField collapsed={materialField === null}>
            {materialSelect}
          </CollapsibleField>
        </Box>
      ) : null}
      {fields.has('fabric') ? fabricRow : null}
      {fields.has('liningFabric') ? (
        <Box sx={rowSx}>
          <RhfFabricSelect<T>
            name={'liningFabric' as Path<T>}
            label="Lining Fabric"
            flag="isMitreLining"
          />
          <RhfColorSelect<T>
            name={'liningColor' as Path<T>}
            label="Lining Color"
            fabricFieldName={'liningFabric' as Path<T>}
          />
        </Box>
      ) : null}
      {fields.has('patching') ? (
        <Box sx={rowSx}>
          <RhfSelect<T>
            name={'patchingType' as Path<T>}
            label="Patching Type"
            options={PATCHING_TYPE_OPTIONS}
          />
          <CollapsibleField collapsed={materialField === null}>
            {materialSelect}
          </CollapsibleField>
        </Box>
      ) : null}
      {children?.(fields)}
    </>
  );
}

export function ChasublePanelTextFields<T extends PanelValues>({
  fields,
}: {
  fields: Set<ChasublePanelField>;
}) {
  return (
    <>
      {fields.has('dimension') ? (
        <RhfTextField<T> name={'panelDimension' as Path<T>} label="Dimension" />
      ) : null}
      {fields.has('stoleSize') ? (
        <RhfTextField<T> name={'stoleSize' as Path<T>} label="Stole Size" />
      ) : null}
    </>
  );
}

export function ChasubleCollarFields<T extends CollarValues>({
  vestment,
  showType = true,
  onTypeChange,
}: {
  vestment?: Vestment;
  showType?: boolean;
  onTypeChange?: () => void;
}) {
  return (
    <Box sx={rowSx}>
      {showType ? (
        <RhfCollarSelect<T>
          name={'collar' as Path<T>}
          label="Type"
          vestment={vestment}
          onValueChange={onTypeChange}
        />
      ) : null}
      <RhfFabricSelect<T> name={'collarFabric' as Path<T>} label="Fabric" />
      <RhfColorSelect<T>
        name={'collarColor' as Path<T>}
        label="Color"
        fabricFieldName={'collarFabric' as Path<T>}
      />
    </Box>
  );
}
